import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';

/**
 * Drives the License wizard's cascading Workfront dropdowns
 * (portfolio → program; template is independent) by proxying the
 * customer's Workfront `attask` API through the admin-console-api
 * `workfront-picker` Lambdalith.
 *
 * Mirrors the Fusion picker hooks. `staleTime: 30s` so re-opening
 * the wizard right after editing doesn't burn an extra round-trip;
 * picker payloads are id+name pairs only, a few KB at most.
 */
const STALE_MS = 30_000;

export interface WorkfrontEntity {
  id: string;
  name: string;
}

export function useWorkfrontPortfoliosQuery(
  clientId: string | undefined,
  workfrontConfigId: string | null | undefined,
): UseQueryResult<WorkfrontEntity[], Error> {
  const api = useApiClient();
  return useQuery({
    queryKey: ['workfront-picker', 'portfolios', clientId, workfrontConfigId] as const,
    enabled: Boolean(api && clientId && workfrontConfigId),
    staleTime: STALE_MS,
    queryFn: async () => {
      if (!api) throw new Error('useWorkfrontPortfoliosQuery: api client unavailable');
      if (!clientId || !workfrontConfigId) {
        throw new Error('useWorkfrontPortfoliosQuery: clientId + workfrontConfigId required');
      }
      const res = await api.get<{ portfolios: WorkfrontEntity[] }>(
        `/v1/clients/${clientId}/workfront-configs/${workfrontConfigId}/portfolios`,
      );
      return res.portfolios;
    },
  });
}

export function useWorkfrontProgramsQuery(
  clientId: string | undefined,
  workfrontConfigId: string | null | undefined,
  portfolioId: string | null | undefined,
): UseQueryResult<WorkfrontEntity[], Error> {
  const api = useApiClient();
  return useQuery({
    queryKey: [
      'workfront-picker',
      'programs',
      clientId,
      workfrontConfigId,
      portfolioId ?? null,
    ] as const,
    enabled: Boolean(api && clientId && workfrontConfigId && portfolioId),
    staleTime: STALE_MS,
    queryFn: async () => {
      if (!api) throw new Error('useWorkfrontProgramsQuery: api client unavailable');
      if (!clientId || !workfrontConfigId || !portfolioId) {
        throw new Error(
          'useWorkfrontProgramsQuery: clientId + workfrontConfigId + portfolioId required',
        );
      }
      const res = await api.get<{ programs: WorkfrontEntity[] }>(
        `/v1/clients/${clientId}/workfront-configs/${workfrontConfigId}/programs?portfolioId=${encodeURIComponent(portfolioId)}`,
      );
      return res.programs;
    },
  });
}

export function useWorkfrontTemplatesQuery(
  clientId: string | undefined,
  workfrontConfigId: string | null | undefined,
): UseQueryResult<WorkfrontEntity[], Error> {
  const api = useApiClient();
  return useQuery({
    queryKey: ['workfront-picker', 'templates', clientId, workfrontConfigId] as const,
    enabled: Boolean(api && clientId && workfrontConfigId),
    staleTime: STALE_MS,
    queryFn: async () => {
      if (!api) throw new Error('useWorkfrontTemplatesQuery: api client unavailable');
      if (!clientId || !workfrontConfigId) {
        throw new Error('useWorkfrontTemplatesQuery: clientId + workfrontConfigId required');
      }
      const res = await api.get<{ templates: WorkfrontEntity[] }>(
        `/v1/clients/${clientId}/workfront-configs/${workfrontConfigId}/templates`,
      );
      return res.templates;
    },
  });
}
